import { useEffect, useState, useRef } from "react";
import restClient from "restClients";
import List from "./components/List";
import ChevronDownIcon from "assets/svg/chevron-down-icon";
import { Tooltip } from "components/tooltip/Tooltip";
import LanguageMenu from "components/header/LanguageMenu";
import { Link } from "react-router-dom";
import ArrowRightIcon from "assets/svg/arrow-right-icon";
import UKFlagIcon from "assets/svg/uk-flag-icon";
// import ChinaFlagIcon from "assets/svg/china-flag-icon";

const languageOptions = [
  {
    value: "en",
    label: "English",
    flag: UKFlagIcon,
  },
  // {
  //   value: "zh",
  //   label: "Chinese (普通话)",
  //   flag: ChinaFlagIcon,
  // },
];

export default function Footer({ scrollToTop }) {
  const [data, setData] = useState({});
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(languageOptions[0]);

  const sectionRef = useRef(null);
  const languageButtonRef = useRef(null);

  const { linksData, bottomTexts, logo, Awards } = data || {};
  console.log("linksData", linksData);
  const getData = async () => {
    try {
      const { data } = await restClient.get("/footer");

      setData(data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getInitialLanguage = () => {
    const storedLocale = localStorage.getItem("locale");
    if (storedLocale) {
      const matchingOption = languageOptions.find(
        (option) =>
          option.value === storedLocale ||
          option.value === storedLocale.split("-")[0]
      );
      if (matchingOption) return matchingOption;
      localStorage.removeItem("locale");
    }

    const browserLang = navigator.language || "en";
    const browserLangCode = browserLang.split("-")[0];
    const matchingOption = languageOptions.find(
      (option) => option.value === browserLangCode
    );

    if (matchingOption) {
      localStorage.setItem("locale", browserLangCode);
      return matchingOption;
    }

    return languageOptions[0];
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const handleLanguageSelect = (option) => {
    if (selectedLanguage.value !== option.value) {
      setSelectedLanguage(option);
      localStorage.setItem("locale", option.value);
      window.location.reload();
    }

    setIsLanguageMenuOpen(false);
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const initialLanguage = getInitialLanguage();
    setSelectedLanguage(initialLanguage);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsContentVisible(true);
          }, 150);
        }
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageButtonRef.current &&
        !languageButtonRef.current.contains(event.target)
      ) {
        setIsLanguageMenuOpen(false);
      }
    };

    if (isLanguageMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageMenuOpen]);

  const FlagIcon = selectedLanguage?.flag;

  return (
    <div
      ref={sectionRef}
      className="w-full h-full bg-primary-black px-[16px] pt-[32px] 3xl:pt-[112px] pb-[32px] xl:px-[3rem] 3xl:px-[5rem] flex flex-col gap-[1.5rem]"
    >
      <div
        className={`grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 md:gap-10 2xl:gap-40 lg:items-start transition-all duration-1000 ease-out ${
          isContentVisible
            ? "translate-y-0 opacity-100"
            : "translate-y-24 opacity-0"
        }`}
      >
        <Link to="/">
          <img
            src={logo?.url}
            alt="the railyard logo"
            className="h-[38px] lg:h-[56px]"
          />
        </Link>

        <div
          className={`grid grid-cols-2 lg:grid-cols-5 lg:flex gap-6 lg:justify-between`}
        >
          {linksData?.map((linkObj, index) => (
            <div
              key={index}
              className={`${
                index > 2 ? "col-span-2 lg:col-span-1" : "col-span-1"
              }`}
            >
              <List data={linkObj} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div
          className={`flex items-center gap-[36px] transition-all duration-1000 ease-out delay-100 ${
            isContentVisible
              ? "translate-y-0 opacity-100"
              : "translate-y-24 opacity-0"
          }`}
        >
          {Awards?.map((award, index) => (
            <Tooltip
              key={index}
              content={award?.description}
              title={award?.title}
            >
              <div key={index} className="w-1/4 md:w-1/6 lg:w-auto">
                <img
                  key={index}
                  src={award?.image?.url}
                  alt={award?.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </Tooltip>
          ))}
        </div>

        <button
          onClick={scrollToTop}
          className={`hidden lg:flex items-center gap-[6px] cursor-pointer lg:hover:opacity-80 transition-all duration-1000 ease-out delay-200 ${
            isContentVisible
              ? "translate-y-0 opacity-100"
              : "translate-y-24 opacity-0"
          }`}
        >
          <div className="w-[24px] h-[24px] bg-pure-white flex items-center justify-center rounded-full">
            <ArrowRightIcon className="w-[12px] h-[12px] text-primary-black -rotate-90" />
          </div>
          <p className="text-[14px] font-medium text-pure-white">
            Scroll to top
          </p>
        </button>
      </div>

      <div
        className={`flex flex-col gap-2 md:flex-row justify-between md:items-center transition-all duration-1000 ease-out delay-200 ${
          isContentVisible
            ? "translate-y-0 opacity-100"
            : "translate-y-24 opacity-0"
        }`}
      >
        <div
          className="flex items-center gap-[8px] relative"
          ref={languageButtonRef}
        >
          <button
            onClick={toggleLanguageMenu}
            className="flex items-center gap-[8px] lg:hover:opacity-80 transition-opacity duration-200"
          >
            {/* <img
              src={selectedLanguage.flag}
              alt={`${selectedLanguage.label} Flag`}
              className="w-[18px] h-[18px]"
            /> */}
            {FlagIcon && <FlagIcon className="w-[18px] h-[18px]" />}
            <div className="flex items-center gap-[4px]">
              <span className="font-medium text-pure-white text-[0.875rem]">
                {selectedLanguage.value.toUpperCase()}
              </span>
              <ChevronDownIcon
                className={`w-[8px] h-[8px] text-pure-white transition-transform duration-200 ${
                  isLanguageMenuOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>

          {isLanguageMenuOpen && (
            <div className="absolute bottom-full left-0 mb-2 z-50 min-w-[200px]">
              <LanguageMenu
                options={languageOptions}
                selectedOption={selectedLanguage}
                onSelect={handleLanguageSelect}
              />
            </div>
          )}
        </div>

        <span
          className={`text-[14px] font-normal text-pure-white/80 transition-all duration-1000 ease-out delay-200 ${
            isContentVisible
              ? "translate-y-0 opacity-100"
              : "translate-y-24 opacity-0"
          }`}
        >
          {bottomTexts?.map((textObj) => textObj.text)?.join(" | ")}
        </span>
        <button
          onClick={scrollToTop}
          className={`flex lg:hidden items-center justify-end gap-[6px] cursor-pointer lg:hover:opacity-80 transition-all duration-1000 ease-out delay-200 ${
            isContentVisible
              ? "translate-y-0 opacity-100"
              : "translate-y-24 opacity-0"
          }`}
        >
          <div className="w-[24px] h-[24px] bg-pure-white flex items-center justify-center rounded-full">
            <ArrowRightIcon className="w-[12px] h-[12px] text-primary-black -rotate-90" />
          </div>
        </button>
      </div>
    </div>
  );
}
